import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { apiErrorMessage, useUpdateProfile } from '../lib/api'
import { useAuth } from '../lib/auth'

const TIMEZONES = [
  'Europe/Budapest', 'Europe/London', 'Europe/Berlin', 'Europe/Madrid',
  'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo', 'UTC',
]

interface NavItem { to: string; label: string; icon: JSX.Element; badge?: string; active?: boolean }

export function SettingsSidebar({ active }: { active: string }) {
  const items: NavItem[] = [
    { to: '/settings', label: 'Profile', active: active === 'profile',
      icon: (<svg className="settings-nav__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="4"/><path d="M4 21 a8 8 0 0 1 16 0"/></svg>) },
    { to: '/settings/security', label: 'Security', active: active === 'security',
      icon: (<svg className="settings-nav__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 3 l8 3 v6 c0 5 -3.5 8 -8 9 c-4.5 -1 -8 -4 -8 -9 V6 z"/><polyline points="9 12 11 14 15 10"/></svg>) },
    { to: '/kyc', label: 'Identity (KYC)', active: active === 'kyc',
      icon: (<svg className="settings-nav__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="11" r="2"/></svg>) },
    { to: '/notifications', label: 'Notifications', active: active === 'notifications',
      icon: (<svg className="settings-nav__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 8 a6 6 0 0 1 12 0 c0 7 3 9 3 9 H3 s3 -2 3 -9"/><path d="M10 21 a2 2 0 0 0 4 0"/></svg>) },
  ]
  return (
    <nav className="settings-nav" aria-label="Settings sections">
      {items.map((i) => (
        <Link key={i.to} to={i.to} className={`settings-nav__item ${i.active ? 'settings-nav__item--active' : ''}`}>
          {i.icon}
          <span>{i.label}</span>
          {i.badge && <span className="settings-nav__badge">{i.badge}</span>}
        </Link>
      ))}
    </nav>
  )
}

export function SettingsPage() {
  const { user } = useAuth()
  const update = useUpdateProfile()

  const [displayName, setDisplayName] = useState('')
  const [locale, setLocale] = useState('hu')
  const [timezone, setTimezone] = useState('Europe/Budapest')

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName ?? '')
      setLocale(user.locale ?? user.language ?? 'hu')
      setTimezone(user.timezone ?? 'Europe/Budapest')
    }
  }, [user])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    update.mutate({ displayName, locale, timezone })
  }

  return (
    <AppShell page="settings" breadcrumb={<span className="topbar__breadcrumb-current">Settings</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Account</span></p>
          <h1 className="page-header__title">Settings</h1>
          <p className="page-header__subtitle">Manage your profile, security, identity verification, and notifications.</p>
        </div>
      </header>

      <div className="settings-layout">
        <SettingsSidebar active="profile" />

        <div className="settings-content">
          <div className="settings-card">
            <div className="settings-card__header">
              <div className="settings-card__title-group">
                <h2 className="settings-card__title">Profile information</h2>
                <p className="settings-card__description">Personal details on file.</p>
              </div>
            </div>
            <div className="settings-card__body">
              <form className="form" onSubmit={submit}>
                <div className="form-section">
                  <div className="form-row">
                    <div className="field">
                      <label className="field__label" htmlFor="settings-name">Display name</label>
                      <input id="settings-name" className="field__input" type="text"
                        value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                    </div>
                    <div className="field field--success">
                      <label className="field__label" htmlFor="settings-email">Email address</label>
                      <input id="settings-email" className="field__input" type="email" value={user?.email ?? ''} disabled />
                      <span className="field__helper">
                        {user?.emailVerifiedAt ? 'Verified · contact support to change.' : 'Not verified yet.'}
                      </span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="field">
                      <label className="field__label" htmlFor="settings-locale">Language</label>
                      <select id="settings-locale" className="field__select"
                        value={locale} onChange={(e) => setLocale(e.target.value)}>
                        <option value="hu">Magyar</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="settings-tz">Timezone</label>
                      <select id="settings-tz" className="field__select"
                        value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                        {TIMEZONES.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
                      </select>
                    </div>
                  </div>

                  {update.isError && (
                    <div className="auth-card__error" role="alert" style={{ display: 'flex' }}>
                      {apiErrorMessage(update.error)}
                    </div>
                  )}
                </div>

                <div className="save-bar">
                  <div className="save-bar__status">
                    {update.isSuccess && <span style={{ color: 'var(--color-success-text)' }}>Saved.</span>}
                  </div>
                  <div className="save-bar__actions">
                    <button type="submit" className="btn btn--primary" disabled={update.isPending}>
                      {update.isPending ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
